/**
 * Reducer for the Onboarding Wizard (Week 4, client-side only).
 *
 * Manages all wizard form state via `useReducer`. Mirrors the immutable,
 * spread-based style of ConfigGenerator/configPageReducer.ts.
 *
 * State flow:
 *   1. SchemaImportStep parses DDL/CSV/JSON -> ColumnsResponse
 *   2. LOAD_COLUMNS stores the columns and derives `sourceTableName`
 *   3. The page calls inferConfig(columns) -> InferenceResult
 *   4. LOAD_INFERENCE pre-fills toolName, category, and base-metric rows
 *   5. The user edits metadata / database / metric rows through the
 *      remaining SET_* / *_METRIC actions
 *
 * No network, no AWS, no I/O — purely client-side state.
 *
 * @see types.ts for the LOCKED Week 4 contract (do not redefine here).
 */

import { DEFAULT_CATALOG_SCHEMA } from './catalogRows';
import {
  AggFunction,
  BaseMetricDraft,
  ColumnsResponse,
  DerivedMetricDraft,
  InferenceResult,
  OnboardingConfig,
  MetricSuggestion,
  ParameterDraft,
  ParameterSuggestion,
} from './types';

// ─── FDA presets ───────────────────────────────────────────────────────
// Fintech Data Analytics standard Redshift connection + v1 stored procedure.

const FDA_CLUSTER_IDENTIFIER = 'fda_svls_cluster';
// The FDA Redshift Serverless workgroup exposes the `fda_svls_db` database. At
// runtime the `database:` field is advisory — the Bluey runtime connects using
// the secret's own `dbname` — but the emitted YAML must still be honest so
// external teams and Spec-4 validation aren't misled.
const FDA_DATABASE = 'fda_svls_db';
const V1_STORED_PROCEDURE = 'sp_bluey_common_report';

// ─── Helpers ─────────────────────────────────────────────────────────────

/**
 * Builds the fully-qualified source table name from a parsed schema.
 * `schema.table` when a schema is present, otherwise just `table`.
 */
export function computeSourceTableName(columns: ColumnsResponse): string {
  return columns.schema ? `${columns.schema}.${columns.tableName}` : columns.tableName;
}

// ─── Initial state ───────────────────────────────────────────────────────

export const initialOnboardingConfig: OnboardingConfig = {
  toolName: '',
  toolType: 'UNIVERSAL_STORED_PROCEDURE',
  category: '',
  // Optional client-side context captured on the Schema Import step. Fed to the
  // LLM metric/description inference in a LATER backend phase. Never gates Next.
  businessContext: '',
  columnNotes: {},
  sourceTableName: '',
  storedProcedure: V1_STORED_PROCEDURE,
  // Schema for the two metric-catalog tables. `bluey_input` is the convention on
  // every real FDA catalog; editable on the Review step.
  catalogSchema: DEFAULT_CATALOG_SCHEMA,
  database: {
    clusterIdentifier: FDA_CLUSTER_IDENTIFIER,
    database: FDA_DATABASE,
    secretArns: { test: '', beta: '', live: '' },
  },
  baseMetrics: [],
  derivedMetrics: [],
  parameters: [],
  // LLM-generated (Spec 6) or hand-written tool description. Never gates Next.
  toolDescription: '',
};

export interface OnboardingState {
  /** Parsed columns from SchemaImportStep, or null before import. */
  columns: ColumnsResponse | null;
  /** Raw inference result (kept for reference / re-seeding metric tables). */
  inference: InferenceResult | null;
  /** The editable draft config the wizard is building. */
  config: OnboardingConfig;
}

export const initialOnboardingState: OnboardingState = {
  columns: null,
  inference: null,
  config: initialOnboardingConfig,
};

// ─── Actions ─────────────────────────────────────────────────────────────

export type SecretEnv = 'test' | 'beta' | 'live';

/**
 * Dropdown options for picking which environment's secret ARN to connect with.
 * Shared by every step that opens a Redshift connection (schema import, the
 * stored-procedure check, the catalog write) so the labels can't drift apart.
 */
export const SECRET_ENV_OPTIONS: ReadonlyArray<{ value: SecretEnv; label: string }> = [
  { value: 'test', label: 'Test' },
  { value: 'beta', label: 'Beta' },
  { value: 'live', label: 'Live (prod)' },
];
export type MetadataField = 'toolName' | 'category';
export type DatabaseField = 'clusterIdentifier' | 'database';
export type BaseMetricField = keyof BaseMetricDraft;
export type DerivedMetricField = keyof DerivedMetricDraft;
export type ParameterField = keyof ParameterDraft;

export type OnboardingAction =
  | { type: 'LOAD_COLUMNS'; columns: ColumnsResponse }
  | { type: 'LOAD_INFERENCE'; inference: InferenceResult }
  | { type: 'SET_METADATA_FIELD'; field: MetadataField; value: string }
  | { type: 'SET_BUSINESS_CONTEXT'; value: string }
  | { type: 'SET_COLUMN_NOTE'; columnName: string; note: string }
  | { type: 'SET_DATABASE_FIELD'; field: DatabaseField; value: string }
  | { type: 'SET_CATALOG_SCHEMA'; value: string }
  | { type: 'SET_SECRET_ARN'; env: SecretEnv; value: string }
  | { type: 'ADD_BASE_METRIC' }
  | { type: 'UPDATE_BASE_METRIC'; index: number; field: BaseMetricField; value: string | boolean }
  | { type: 'REMOVE_BASE_METRIC'; index: number }
  | { type: 'ADD_DERIVED_METRIC' }
  | { type: 'UPDATE_DERIVED_METRIC'; index: number; field: DerivedMetricField; value: string }
  | { type: 'REMOVE_DERIVED_METRIC'; index: number }
  | { type: 'APPLY_SUGGESTED_METRICS'; suggestion: MetricSuggestion }
  | { type: 'ADD_PARAMETER' }
  | {
      type: 'UPDATE_PARAMETER';
      index: number;
      field: ParameterField;
      value: string | boolean | string[] | null;
    }
  | { type: 'REMOVE_PARAMETER'; index: number }
  | { type: 'APPLY_SUGGESTED_PARAMETERS'; suggestion: ParameterSuggestion }
  | { type: 'SET_TOOL_DESCRIPTION'; value: string };

// ─── Factories for blank rows ──────────────────────────────────────────

function emptyBaseMetric(): BaseMetricDraft {
  // Hand-added rows are user-facing by default — the user added them to be
  // queried. publish=false is reserved for LLM-proposed dependency-only helpers.
  return { metricKey: '', exprSql: '', agg: 'SUM', filterCondition: null, publish: true };
}

function emptyDerivedMetric(): DerivedMetricDraft {
  return { derivedMetricKey: '', baseMetricsCsv: '', exprSql: '', description: '' };
}

export function emptyParameter(): ParameterDraft {
  return {
    parameterName: '',
    type: 'string',
    description: '',
    required: false,
    enumValues: null,
    defaultValue: null,
  };
}

// ─── Reducer ─────────────────────────────────────────────────────────────

export function onboardingReducer(
  state: OnboardingState,
  action: OnboardingAction,
): OnboardingState {
  switch (action.type) {
    // ── Schema import ────────────────────────────────────────────────
    // NOTE: We intentionally PRESERVE businessContext and columnNotes here
    // rather than resetting them. In the wizard flow the Schema Import step
    // renders the parsed-columns table (with the editable Notes column) and
    // the dataset-description textarea BEFORE the user clicks Next — and Next
    // is what dispatches LOAD_COLUMNS. Resetting would therefore wipe context
    // the user just typed. Any stale note keys from a different table are
    // harmless: they are optional metadata keyed by columnName and unused
    // columns are simply ignored downstream.
    case 'LOAD_COLUMNS':
      return {
        ...state,
        columns: action.columns,
        config: {
          ...state.config,
          sourceTableName: computeSourceTableName(action.columns),
        },
      };

    // ── Auto-inference pre-fill ──────────────────────────────────────
    case 'LOAD_INFERENCE':
      return {
        ...state,
        inference: action.inference,
        config: {
          ...state.config,
          // Pre-fill name + category from the inferred draft, but keep any
          // value the user may have already typed if inference returns blanks.
          toolName: action.inference.toolName || state.config.toolName,
          category: action.inference.category || state.config.category,
          // Seed base-metric rows from the inferred candidates (deep copy so
          // later edits don't mutate the stored inference result).
          baseMetrics: action.inference.metricCandidates.map((m) => ({ ...m })),
        },
      };

    // ── Tool metadata ────────────────────────────────────────────────
    case 'SET_METADATA_FIELD':
      return {
        ...state,
        config: { ...state.config, [action.field]: action.value },
      };

    // ── Dataset context (client-side capture for later LLM inference) ─
    case 'SET_BUSINESS_CONTEXT':
      return {
        ...state,
        config: { ...state.config, businessContext: action.value },
      };

    case 'SET_COLUMN_NOTE':
      // Immutable update: a brand-new columnNotes object per edit so the old
      // reference is never mutated and other column keys are preserved.
      return {
        ...state,
        config: {
          ...state.config,
          columnNotes: {
            ...state.config.columnNotes,
            [action.columnName]: action.note,
          },
        },
      };

    // ── Database config ──────────────────────────────────────────────
    case 'SET_DATABASE_FIELD':
      return {
        ...state,
        config: {
          ...state.config,
          database: { ...state.config.database, [action.field]: action.value },
        },
      };

    case 'SET_CATALOG_SCHEMA':
      return { ...state, config: { ...state.config, catalogSchema: action.value } };

    case 'SET_SECRET_ARN':
      return {
        ...state,
        config: {
          ...state.config,
          database: {
            ...state.config.database,
            secretArns: {
              ...state.config.database.secretArns,
              [action.env]: action.value,
            },
          },
        },
      };

    // ── Base metrics ─────────────────────────────────────────────────
    case 'ADD_BASE_METRIC':
      return {
        ...state,
        config: {
          ...state.config,
          baseMetrics: [...state.config.baseMetrics, emptyBaseMetric()],
        },
      };

    case 'UPDATE_BASE_METRIC': {
      const { index, field, value } = action;
      if (index < 0 || index >= state.config.baseMetrics.length) return state;
      return {
        ...state,
        config: {
          ...state.config,
          baseMetrics: state.config.baseMetrics.map((m, i) =>
            i === index
              ? { ...m, [field]: field === 'agg' ? (value as AggFunction) : value }
              : m,
          ),
        },
      };
    }

    case 'REMOVE_BASE_METRIC':
      return {
        ...state,
        config: {
          ...state.config,
          baseMetrics: state.config.baseMetrics.filter((_, i) => i !== action.index),
        },
      };

    // ── Derived metrics ──────────────────────────────────────────────
    case 'ADD_DERIVED_METRIC':
      return {
        ...state,
        config: {
          ...state.config,
          derivedMetrics: [...state.config.derivedMetrics, emptyDerivedMetric()],
        },
      };

    case 'UPDATE_DERIVED_METRIC': {
      const { index, field, value } = action;
      if (index < 0 || index >= state.config.derivedMetrics.length) return state;
      return {
        ...state,
        config: {
          ...state.config,
          derivedMetrics: state.config.derivedMetrics.map((m, i) =>
            i === index ? { ...m, [field]: value } : m,
          ),
        },
      };
    }

    case 'REMOVE_DERIVED_METRIC':
      return {
        ...state,
        config: {
          ...state.config,
          derivedMetrics: state.config.derivedMetrics.filter((_, i) => i !== action.index),
        },
      };

    // ── AI-suggested metrics (propose-confirm) ───────────────────────
    case 'APPLY_SUGGESTED_METRICS':
      return {
        ...state,
        config: {
          ...state.config,
          baseMetrics: action.suggestion.baseMetrics.map((m) => ({ ...m })),
          derivedMetrics: action.suggestion.derivedMetrics.map((m) => ({ ...m })),
        },
      };

    // ── Parameters (input_schema) ────────────────────────────────────
    case 'ADD_PARAMETER':
      return {
        ...state,
        config: {
          ...state.config,
          parameters: [...state.config.parameters, emptyParameter()],
        },
      };

    case 'UPDATE_PARAMETER': {
      const { index, field, value } = action;
      if (index < 0 || index >= state.config.parameters.length) return state;
      return {
        ...state,
        config: {
          ...state.config,
          parameters: state.config.parameters.map((p, i) =>
            i === index ? { ...p, [field]: value } : p,
          ),
        },
      };
    }

    case 'REMOVE_PARAMETER':
      return {
        ...state,
        config: {
          ...state.config,
          parameters: state.config.parameters.filter((_, i) => i !== action.index),
        },
      };

    // ── AI-suggested parameters (propose-confirm) ────────────────────
    case 'APPLY_SUGGESTED_PARAMETERS':
      return {
        ...state,
        config: {
          ...state.config,
          // Deep-copy each row (incl. enumValues + raw) so later edits don't
          // mutate the source suggestion.
          parameters: action.suggestion.parameters.map((p) => ({
            ...p,
            enumValues: p.enumValues ? [...p.enumValues] : null,
            raw: p.raw ? { ...p.raw } : undefined,
          })),
        },
      };

    // ── Tool description (Spec 6; LLM-generated or hand-written) ─────
    case 'SET_TOOL_DESCRIPTION':
      return {
        ...state,
        config: { ...state.config, toolDescription: action.value },
      };

    default:
      return state;
  }
}
